/**
 * NativeVideoPlayer
 *
 * Owner-agnostic player for native (Cloudflare-hosted) videos. Renders the
 * official <Stream> component. The caller mints playback via `fetchToken`,
 * which returns a signed token for PRIVATE owners or `{ token: null }` for
 * PUBLIC owners (Cloudflare plays those off the bare UID). Re-fetches on
 * playback error (the CF player doesn't auto-renew tokens) up to a small cap.
 * Non-ready rows render a status placeholder.
 *
 * The owner-specific wrappers (ProfileVideoPlayer, CommunityVideoPlayer) bind
 * `fetchToken` to their endpoint; this component owns all the playback +
 * retry + remount logic so it lives once.
 */

import { Stream, type StreamPlayerApi } from "@cloudflare/stream-react"
import { useCallback, useEffect, useRef, useState } from "react"

export interface NativeVideoPlayerProps {
  status: string
  cloudflareUid: string | null
  /** Surfaced only to managers/owners (callers pass null otherwise). */
  failureReason: string | null
  /** Mints fresh playback: a signed token for PRIVATE owners, or { token: null }
   *  for PUBLIC owners (played off the bare UID). MUST be stable (useCallback)
   *  so the fetch/retry effects don't churn. */
  fetchToken: () => Promise<{ token: string | null }>
  /** When false, the token is NOT minted (lazy-mint for off-screen carousel
   *  slides). Defaults to true. */
  active?: boolean
  className?: string
}

const NonReadyPlaceholder = ({
  status,
  failureReason,
}: {
  status: string
  failureReason: string | null
}) => {
  if (status === "failed") {
    return (
      <div className="flex h-full w-full items-center justify-center bg-black p-8 text-white">
        <div className="text-center">
          <p className="mb-2 font-semibold text-lg">Video unavailable</p>
          <p className="text-sm opacity-75">
            {failureReason ?? "Processing failed. Please re-upload."}
          </p>
        </div>
      </div>
    )
  }
  const label =
    status === "pending"
      ? "Queued for processing"
      : status === "processing"
        ? "Adding branded intro"
        : "Preparing video"
  return (
    <div className="flex h-full w-full items-center justify-center bg-black text-white">
      <p className="text-sm opacity-90">{label}...</p>
    </div>
  )
}

export function NativeVideoPlayer({
  status,
  cloudflareUid,
  failureReason,
  fetchToken,
  active = true,
  className,
}: NativeVideoPlayerProps) {
  const MAX_TOKEN_RETRIES = 2
  const streamRef = useRef<StreamPlayerApi | undefined>(undefined)
  const lastPlaybackTimeRef = useRef<number>(0)
  const tokenRetryCountRef = useRef<number>(0)
  // null until fetchToken resolves; once resolved, `.token` is the signed token
  // (PRIVATE) or null (PUBLIC -> play off the bare UID). The wrapper object lets
  // us tell "not fetched yet" apart from "fetched, public, no token".
  const [playback, setPlayback] = useState<{ token: string | null } | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)
  // Bumped on each deliberate retry to force a fresh <Stream> mount even when
  // the backend hands back the SAME src (decoupled from src identity).
  const [remountKey, setRemountKey] = useState(0)

  const isReady = status === "ready" && cloudflareUid !== null

  // NOTE: deliberately does NOT reset tokenRetryCountRef on success -- a flaky
  // stream that errors -> refetch(succeeds) -> errors again must still
  // accumulate toward the cap (the reset lives only in the isReady effect).
  const doFetchToken = useCallback(async () => {
    try {
      const result = await fetchToken()
      setPlayback({ token: result.token })
      setTokenError(null)
    } catch (error) {
      setTokenError(error instanceof Error ? error.message : "Could not load video")
      setPlayback(null)
    }
  }, [fetchToken])

  useEffect(() => {
    // Lazy-mint: only fetch a token for an active (on-screen) ready slide, so a
    // carousel of N videos doesn't fire N token requests on load.
    if (isReady && active) {
      tokenRetryCountRef.current = 0
      void doFetchToken()
    }
  }, [isReady, active, doFetchToken])

  // Pause the stream when this slide becomes inactive (user scrolled away in a carousel)
  useEffect(() => {
    if (!active && streamRef.current) {
      streamRef.current.pause()
    }
  }, [active])

  const handleStreamError = useCallback(() => {
    const t = streamRef.current?.currentTime
    if (typeof t === "number" && Number.isFinite(t)) {
      lastPlaybackTimeRef.current = t
    }
    if (tokenRetryCountRef.current >= MAX_TOKEN_RETRIES) {
      setTokenError("Playback keeps failing. Please refresh the page.")
      return
    }
    tokenRetryCountRef.current += 1
    setRemountKey(k => k + 1)
    // A signed (PRIVATE) stream may have errored on an expired token -> refetch.
    // A PUBLIC stream plays off the bare UID (token null), so refetching mints
    // another null and can't help; just remount to clear a transient hiccup.
    if (playback?.token != null) {
      void doFetchToken()
    }
  }, [doFetchToken, playback])

  if (!isReady) {
    return (
      <div className={`h-full w-full bg-black ${className ?? ""}`}>
        <NonReadyPlaceholder status={status} failureReason={failureReason} />
      </div>
    )
  }

  // Signed token for PRIVATE owners; the bare UID for PUBLIC (token null). isReady
  // guarantees cloudflareUid is non-null, so this resolves to a valid src.
  const streamSrc = playback?.token ?? cloudflareUid

  if (!playback || !streamSrc) {
    return (
      <div className={`h-full w-full bg-black ${className ?? ""}`}>
        <div className="flex h-full w-full items-center justify-center text-white">
          <p className="text-sm opacity-90">
            {tokenError ? `Unable to load: ${tokenError}` : "Loading playback..."}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={`h-full w-full bg-black ${className ?? ""}`}>
      <Stream
        // remountKey (not the src) forces a fresh mount on a deliberate retry,
        // so a same-valued src still clears a stuck error frame.
        key={remountKey}
        src={streamSrc}
        streamRef={streamRef}
        controls
        startTime={lastPlaybackTimeRef.current}
        onError={handleStreamError}
        width="100%"
        height="100%"
        // responsive=false + h-full/w-full so the iframe fills its slot instead
        // of collapsing (see the original RitualVideoPlayer for the why).
        className="h-full w-full"
        responsive={false}
      />
    </div>
  )
}
